/**
 * Página PÚBLICA de un link temporal (F5): /vault/s/:linkId, fuera del
 * ProtectedRoute. Estática al cargar (el GET nunca revela — si no, el unfurl de
 * Slack/WhatsApp quema el link de un solo uso); el reveal es un POST con click
 * explícito a VaultShareLinkFunction. Lo que se muestra se oculta a los 5 min.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, Eye, KeyRound, Lock, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logo from '@/assets/winclap-logo.svg';
import { useCountdown } from '@/hooks/useCountdown';
import type { ShareLinkRevealResponse } from '../../../../shared/types/vault';

type Revealed = ShareLinkRevealResponse;

const HIDE_AFTER_MS = 5 * 60_000;

export function ShareLinkPage() {
  const { t } = useTranslation();
  const { linkId = '' } = useParams();
  const [state, setState] = useState<'idle' | 'loading' | 'shown' | 'error'>('idle');
  const [data, setData] = useState<Revealed | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const errorText = (code: string) =>
    ({
      not_found: t('vault.share.err_not_found', 'Este link no existe o ya venció.'),
      expired: t('vault.share.err_expired', 'Este link venció.'),
      consumed: t('vault.share.err_consumed', 'Este link ya fue usado (era de un solo uso).'),
      revoked: t('vault.share.err_revoked', 'Este link fue revocado.'),
      rate_limited: t('vault.share.err_rate', 'Demasiados intentos desde esta red. Probá en un minuto.'),
    })[code] ?? t('vault.share.err_generic', 'No se pudo abrir el link.');

  const reveal = async () => {
    setState('loading');
    setError(null);
    try {
      const res = await fetch(import.meta.env.VITE_VAULT_SHARE_URL as string, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: linkId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(errorText(body?.error ?? 'generic'));
        setState('error');
        return;
      }
      setData(body as Revealed);
      setState('shown');
    } catch {
      setError(errorText('generic'));
      setState('error');
    }
  };

  useEffect(() => {
    if (state !== 'shown') return;
    const id = window.setTimeout(() => { setData(null); setState('idle'); }, HIDE_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [state]);

  // El código MFA es una foto del momento del reveal: se cuenta hacia atrás y al
  // vencer se dice (antes quedaba "válido 45 s" congelado hasta 5 minutos).
  const totpLeft = useCountdown(data?.totp ? data.totp.expiresIn : null, data);

  // La cáscara pública no debe indexarse (el header X-Robots-Tag lo pone la
  // Lambda solo en el JSON; la SPA se sirve desde S3/CloudFront).
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      /* portapapeles bloqueado: el valor está visible igual */
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-offwhite p-4">
      <div className="w-full max-w-md rounded-2xl border border-brand-gray4 bg-white p-8 shadow-xl">
        <img src={logo} alt="Winclap" className="mb-6 h-5 w-auto" />
        <div className="mb-4 flex items-center gap-2 text-sm text-brand-gray3">
          <KeyRound className="h-4 w-4" />
          {t('vault.share.kicker', 'Credencial compartida desde el Vault de Winclap')}
        </div>

        {state === 'idle' || state === 'loading' ? (
          <>
            <h1 className="font-display text-2xl text-brand-gray1">{t('vault.share.title', 'Alguien te compartió una credencial')}</h1>
            <p className="mt-2 text-sm text-brand-gray2">
              {t('vault.share.body', 'Al tocar el botón se muestra por 5 minutos. Si el link es de un solo uso, después de eso deja de funcionar — no lo abras hasta estar listo para copiarla.')}
            </p>
            <button
              type="button"
              onClick={reveal}
              disabled={state === 'loading'}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-brand-gray1 px-4 py-3 text-sm font-medium text-white hover:bg-[#2A2A2A] disabled:opacity-60"
            >
              <Eye className="h-4 w-4" />
              {state === 'loading' ? t('vault.share.opening', 'Abriendo…') : t('vault.share.cta', 'Ver credencial')}
            </button>
          </>
        ) : state === 'error' ? (
          <>
            <div className="flex items-start gap-3 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
            <p className="mt-4 text-xs text-brand-gray3">{t('vault.share.ask_again', 'Pedile a quien te lo mandó que genere uno nuevo.')}</p>
          </>
        ) : data ? (
          <>
            <h1 className="font-display text-xl text-brand-gray1">{data.name}</h1>
            <p className="mt-1 text-xs text-brand-gray3">
              {t('vault.share.from', 'Compartida por {{who}} · se oculta en 5 min', { who: data.createdBy })}
            </p>
            <dl className="mt-5 space-y-4 text-sm">
              <Row label={t('vault.share.username', 'Usuario')} value={data.username} onCopy={() => copy(data.username, 'u')} copied={copied === 'u'} />
              <Row label={t('vault.share.password', 'Contraseña')} value={data.password} mono onCopy={() => copy(data.password, 'p')} copied={copied === 'p'} />
              {data.totp && totpLeft > 0 && (
                <Row label={t('vault.share.totp', 'Código MFA (válido {{s}} s)', { s: totpLeft })} value={data.totp.code} mono onCopy={() => copy(data.totp!.code, 't')} copied={copied === 't'} />
              )}
              {data.totp && totpLeft <= 0 && (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-brand-gray3">{t('vault.share.totp_expired_label', 'Código MFA')}</dt>
                  <dd className="mt-1 text-sm text-amber-700">{t('vault.share.totp_expired', 'El código venció: cambia cada 30 segundos. Pedile otro link a quien te lo compartió.')}</dd>
                </div>
              )}
              {data.url && (
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-brand-gray3">URL</dt>
                  <dd className="mt-1 truncate font-mono text-sm text-sky-600"><a href={data.url} target="_blank" rel="noreferrer">{data.url}</a></dd>
                </div>
              )}
            </dl>
            <p className="mt-6 flex items-center gap-2 text-xs text-brand-gray3">
              <Lock className="h-3 w-3" />
              {t('vault.share.audited', 'Esta apertura quedó registrada (fecha e IP) para quien la compartió.')}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Row({ label, value, mono, onCopy, copied }: { label: string; value: string; mono?: boolean; onCopy: () => void; copied: boolean }) {
  const { t } = useTranslation();
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-brand-gray3">{label}</dt>
      <dd className="mt-1 flex items-center gap-2">
        <code className={`min-w-0 flex-1 truncate rounded-md bg-[#F5F5F5] px-3 py-2 ${mono ? 'font-mono' : ''}`}>{value}</code>
        <button type="button" onClick={onCopy} className="rounded-md border border-brand-gray4 p-2 hover:bg-brand-offwhite" title={t('vault.reveal.copy', 'Copiar')}>
          <Copy className="h-4 w-4" />
        </button>
        {copied && <span className="text-xs text-[#0D9488]">{t('vault.toast.copied', 'Copiado')}</span>}
      </dd>
    </div>
  );
}
